'use client';

import { useEffect } from 'react';

const NDR_DOMAIN = 'nationaldebtrelief.com';
const DEFAULT_PARAMS = 'src=fortune.google.organic';

/**
 * Client component for NDR branded article pages (reviews-3/4).
 * These pages are CMS-driven and don't have React-level query param forwarding,
 * so this component handles it client-side by modifying NDR outbound links
 * to include the current URL's query parameters.
 */
// biome-ignore lint/style/useNamingConvention: React component must be PascalCase
export function NdrBrandedParamForwarder() {
  useEffect(() => {
    const queryString = window.location.search ? window.location.search.slice(1) : DEFAULT_PARAMS;

    const incomingParams = new URLSearchParams(queryString);

    function updateNdrLinks() {
      const links = document.querySelectorAll<HTMLAnchorElement>(`a[href*="${NDR_DOMAIN}"]`);

      for (const anchor of links) {
        try {
          const url = new URL(anchor.href);
          incomingParams.forEach((value, key) => {
            url.searchParams.set(key, value);
          });
          anchor.href = url.toString();

          if (anchor.target === '_blank') {
            anchor.removeAttribute('target');
          }
        } catch {
          // skip malformed URLs
        }
      }
    }

    updateNdrLinks();

    const observer = new MutationObserver((mutations) => {
      const hasRelevantChanges = mutations.some((m) =>
        Array.from(m.addedNodes).some(
          (node) =>
            node.nodeType === 1 &&
            ((node as Element).tagName === 'A' || (node as Element).querySelector?.(`a[href*="${NDR_DOMAIN}"]`)),
        ),
      );
      if (hasRelevantChanges) {
        updateNdrLinks();
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, []);

  return null;
}
